import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';

const CHANNEL_OPTIONS = [
  { value: 'sms', label: 'SMS Only' },
  { value: 'whatsapp', label: 'WhatsApp Only' },
  { value: 'both', label: 'Both SMS & WhatsApp' },
];
const EVENT_TYPE_OPTIONS = ['Wedding', 'Birthday', 'Corporate', 'Other'];

const TemplateManagementPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    template_id: '',
    template_name: '',
    channel: 'both',
    event_type: 'Wedding',
    message_body: '',
    variables: [],
    description: '',
    whatsapp_template_name: '',
  });
  const [newVariable, setNewVariable] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/templates`, {
        credentials: 'include',
      });

      if (!resp.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await resp.json();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      template_id: '',
      template_name: '',
      channel: 'both',
      event_type: 'Wedding',
      message_body: '',
      variables: [],
      description: '',
      whatsapp_template_name: '',
    });
    setNewVariable('');
  };

  const openCreateForm = () => {
    resetForm();
    setEditingTemplate(null);
    setShowCreateForm(true);
  };

  const openEditForm = (template) => {
    setFormData({
      template_id: template.template_id,
      template_name: template.template_name || '',
      channel: template.channel || 'both',
      event_type: template.event_type || 'Wedding',
      message_body: template.message_body || '',
      variables: template.variables || [],
      description: template.description || '',
      whatsapp_template_name: template.whatsapp_template_name || '',
    });
    setEditingTemplate(template);
    setShowCreateForm(true);
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingTemplate(null);
    resetForm();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addVariable = () => {
    const trimmed = newVariable.trim();
    if (!trimmed || formData.variables.includes(trimmed)) return;
    setFormData(prev => ({
      ...prev,
      variables: [...prev.variables, trimmed]
    }));
    setNewVariable('');
  };

  const removeVariable = (index) => {
    setFormData(prev => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const isEdit = !!editingTemplate;
      const url = isEdit
        ? `${CONFIG.apiBaseUrl}/admin/templates/${encodeURIComponent(editingTemplate.template_id)}`
        : `${CONFIG.apiBaseUrl}/admin/templates`;

      const payload = isEdit
        ? {
            template_name: formData.template_name,
            channel: formData.channel,
            event_type: formData.event_type,
            message_body: formData.message_body,
            variables: formData.variables.length > 0 ? formData.variables : undefined,
            description: formData.description || undefined,
            whatsapp_template_name: formData.whatsapp_template_name || undefined,
          }
        : formData;

      const resp = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || data.message || `Failed to ${isEdit ? 'update' : 'create'} template`);
      }

      closeForm();
      fetchTemplates();
    } catch (err) {
      console.error('Save failed:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm(`Are you sure you want to delete template "${templateId}"?`)) {
      return;
    }

    try {
      setError(null);
      const resp = await fetch(`${CONFIG.apiBaseUrl}/admin/templates/${encodeURIComponent(templateId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to delete template');
      }

      fetchTemplates();
    } catch (err) {
      console.error('Delete failed:', err);
      setError(err.message);
    }
  };

  const getChannelLabel = (channel) => {
    const opt = CHANNEL_OPTIONS.find(o => o.value === channel);
    return opt ? opt.label : channel;
  };

  return (
    <div className="admin-page">
      <h2 className="page-title">Message Templates</h2>
      <p className="page-subtitle">
        Create and manage reusable message templates for SMS and WhatsApp.
      </p>

      {error && <div className="error-message">{error}</div>}

      {!showCreateForm && (
        <div className="settings-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Templates</h3>
          </div>

          {loading ? (
            <div className="loading-message">Loading templates...</div>
          ) : templates.length === 0 ? (
            <div className="empty-state">
              <p>No templates created yet. Create your first template to get started.</p>
            </div>
          ) : (
            <div className="event-list">
              {[...templates].sort((a, b) => (a.template_name || '').localeCompare(b.template_name || '')).map(template => (
                <div key={template.template_id} className="event-card">
                  <div className="event-card-header">
                    <div>
                      <h4 className="event-card-title">{template.template_name}</h4>
                    </div>
                    <div className="event-card-actions">
                      <button onClick={() => openEditForm(template)} className="btn btn-secondary btn-sm">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(template.template_id)} className="btn btn-danger btn-sm">
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="event-card-details">
                    <div className="event-detail">
                      <span className="detail-label">Channel:</span>
                      <span className={`channel-badge ${template.channel}`}>{getChannelLabel(template.channel)}</span>
                    </div>
                    <div className="event-detail">
                      <span className="detail-label">Message:</span>
                      <span className="template-body-preview">
                        {template.message_body && template.message_body.length > 200
                          ? template.message_body.substring(0, 200) + '...'
                          : template.message_body}
                      </span>
                    </div>
                    {template.description && (
                      <div className="event-detail">
                        <span className="detail-label">Description:</span>
                        <span className="detail-value">{template.description}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <br />
          <div>
            <button onClick={openCreateForm} className="btn btn-primary">
              + New Template
            </button>
          </div>

        </div>
      )}

      {showCreateForm && (
        <div className="settings-section">
          <h3>{editingTemplate ? 'Edit Template' : 'Create New Template'}</h3>
          <form onSubmit={handleSubmit}>
            {!editingTemplate && (
              <div className="form-group">
                <label className="form-label">Template ID (slug)</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.template_id}
                  onChange={(e) => handleInputChange('template_id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="e.g., wedding-reminder-sms"
                  required
                  pattern="[a-z0-9][a-z0-9-]{2,48}[a-z0-9]"
                  title="4-50 characters, lowercase letters, numbers, and hyphens only"
                />
                <div className="input-hint">Lowercase letters, numbers, and hyphens only. This cannot be changed later.</div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Template Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.template_name}
                onChange={(e) => handleInputChange('template_name', e.target.value)}
                placeholder="e.g., Wedding Save the Date"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Channel</label>
              <select
                className="form-input"
                value={formData.channel}
                onChange={(e) => handleInputChange('channel', e.target.value)}
                required
              >
                {CHANNEL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="input-hint">Which messaging channel this template is designed for.</div>
            </div>

            {(formData.channel === 'whatsapp' || formData.channel === 'both') && (
              <div className="form-group">
                <label className="form-label">WhatsApp Template Name <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.whatsapp_template_name}
                  onChange={(e) => handleInputChange('whatsapp_template_name', e.target.value)}
                  placeholder="e.g., event_reminder"
                  required
                />
                <div className="input-hint">The exact template name registered in your WhatsApp Business account. Used in the API payload.</div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Event Type</label>
              <select
                className="form-input"
                value={formData.event_type}
                onChange={(e) => handleInputChange('event_type', e.target.value)}
                required
              >
                {EVENT_TYPE_OPTIONS.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Message Body</label>
              <textarea
                className="form-input"
                value={formData.message_body}
                onChange={(e) => handleInputChange('message_body', e.target.value)}
                placeholder="Dear {{guest_name}}, you are invited to {{bride_name}} and {{groom_name}}'s wedding..."
                rows={5}
                required
              />
              <div className="input-hint">{'Use {{variable_name}} for dynamic placeholders.'}</div>
            </div>

            <div className="form-group">
              <label className="form-label">Variables</label>
              <div className="variables-list">
                {formData.variables.map((variable, index) => (
                  <div key={index} className="variable-row">
                    <span className="variable-key">{`{{${variable}}}`}</span>
                    <button type="button" onClick={() => removeVariable(index)} className="btn btn-danger btn-sm">
                      X
                    </button>
                  </div>
                ))}
              </div>
              <div className="variable-add-row">
                <input
                  type="text"
                  className="form-input form-input-sm"
                  value={newVariable}
                  onChange={(e) => setNewVariable(e.target.value)}
                  placeholder="Variable name (e.g., bride_name)"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVariable(); } }}
                />
                <button type="button" onClick={addVariable} className="btn btn-secondary btn-sm" disabled={!newVariable.trim()}>
                  Add
                </button>
              </div>
              <div className="input-hint">Variable names used in the message body (e.g., bride_name, venue).</div>
            </div>

            <div className="form-group">
              <label className="form-label">Description (Optional)</label>
              <textarea
                className="form-input"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Admin notes about when to use this template..."
                rows={2}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (editingTemplate ? 'Update Template' : 'Create Template')}
              </button>
              <button type="button" onClick={closeForm} className="btn btn-secondary" disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TemplateManagementPage;
